/**
 * Stable internal ids. Display names live in @sd/i18n, so renaming a faction
 * or resource never touches game logic or saved data.
 */
export const FACTION_IDS = ['earthborn', 'reforged', 'drifters', 'skyborn'] as const;
export type FactionId = (typeof FACTION_IDS)[number];

export const RESOURCE_IDS = [
  'metals',
  'helium3',
  'exotics',
  'energy',
  'population',
  'science',
  'food',
  'alloys',
  'electronics',
  'currency',
] as const;
export type ResourceId = (typeof RESOURCE_IDS)[number];

/** Goods that factions produce cheaply and trade (GDD §4, §5). */
export type GoodId = Extract<ResourceId, 'food' | 'alloys' | 'electronics' | 'helium3'>;

/** Zones of a star system from the centre outwards (GDD §3). */
export const ZONE_IDS = ['corona', 'hot', 'habitable', 'belt', 'giants', 'outer'] as const;
export type ZoneId = (typeof ZONE_IDS)[number];

export const STAR_CLASS_IDS = ['G', 'K', 'M', 'F', 'redGiant', 'binary'] as const;
export type StarClassId = (typeof STAR_CLASS_IDS)[number];

/** Kinds of planets the generator places (GDD §3). */
export const PLANET_KIND_IDS = [
  'lava',
  'desert',
  'terran',
  'asteroids',
  'gasGiant',
  'ice',
] as const;
export type PlanetKind = (typeof PLANET_KIND_IDS)[number];

/** Buildings of a planet (GDD §5). */
export const BUILDING_IDS = [
  'spaceport',
  'commerceCenter',
  'researchCenter',
  'agroComplex',
  'miningComplex',
  'smelter',
  'electronicsFactory',
  'powerPlant',
  'housing',
  'garrison',
  'planetaryShield',
  'groundGuns',
] as const;
export type BuildingId = (typeof BUILDING_IDS)[number];

/** Building slots of a planet: on the surface and in orbit (GDD §5). */
export type SlotLayer = 'surface' | 'orbit';

/** Ship classes (GDD §8). Faction uniques: trade liner (Earthborn), raider (Reforged). */
export const SHIP_CLASS_IDS = [
  'explorer',
  'transport',
  'corvette',
  'frigate',
  'cruiser',
  'tradeLiner',
  'raider',
  'lander',
] as const;
export type ShipClassId = (typeof SHIP_CLASS_IDS)[number];

/** Technologies (GDD §10, «Дерево технологий · MVP»), grouped by branch. */
export const TECH_IDS = [
  'automation',
  'deepMining',
  'fusionReactors',
  'orbitalInfrastructure',
  'nanofactories',
  'tradeProtocols',
  'agrogenetics',
  'financialEngineering',
  'tradeAlgorithms',
  'interstellarFinance',
  'scannerMk2',
  'appliedScience',
  'xenomaterials',
  'scannerMk3',
  'stellarCartography',
  'defenseSystems',
  'heavyHulls',
  'fleetLogistics',
  'orbitalAssault',
  'stellarMeteorology',
  'magneticScreens',
  'starControl',
] as const;
export type TechId = (typeof TECH_IDS)[number];

export const TECH_BRANCH_IDS = [
  'industry',
  'trade',
  'exploration',
  'fleet',
  'resilience',
  'final',
] as const;
export type TechBranchId = (typeof TECH_BRANCH_IDS)[number];
